import { z } from 'zod';

import {
  isLongQuestion,
  isLongResponse,
  LongQuestion,
  LongQuestionResponse,
  QuestionResponse,
  TypedQuestion
} from 'types/question-response.types';

import { QuestionResponseFormValue, IsRequired } from '../base.types';

type BaseLongQuestionResponseFormValue = QuestionResponseFormValue<LongQuestionResponse, 'value'>;
type NonEmptyLongQuestionResponseFormValue = BaseLongQuestionResponseFormValue & {
  value: string;
};

export type FormValue = BaseLongQuestionResponseFormValue | NonEmptyLongQuestionResponseFormValue;

export const isNonEmpty = (
  response: FormValue
): response is NonEmptyLongQuestionResponseFormValue => {
  const { value } = response;
  return value !== undefined && value.length > 0;
};

export const submissionValue = ({
  type,
  value
}: NonEmptyLongQuestionResponseFormValue): Omit<LongQuestionResponse, 'id'> => {
  return { type, value };
};

export const schema = ({ type }: LongQuestion): z.ZodType<FormValue> => {
  const baseSchema = z.object({ type: z.literal(type) });
  const valueSchema = z.discriminatedUnion('req', [
    z.object({
      value: z.string().nonempty('This question is required.'),
      req: z.literal(IsRequired.Req)
    }),
    z.object({
      value: z.string().optional(),
      req: z.literal(IsRequired.Opt)
    })
  ]);
  return baseSchema.and(valueSchema);
};

export const initialize: (question: LongQuestion, response?: LongQuestionResponse) => FormValue = (
  { type, isRequired: required },
  response
) => {
  const req = required ? IsRequired.Req : IsRequired.Opt;
  const value = response?.value;
  return { type, req, value };
};

export const validateAndInitialize = (
  question: TypedQuestion,
  response?: QuestionResponse
): FormValue | undefined => {
  if (!isLongQuestion(question)) return undefined;
  if (response && isLongResponse(response)) return initialize(question, response);
  return initialize(question);
};

export const validateAndSchematize = (
  question: TypedQuestion
): z.ZodType<FormValue> | undefined => {
  return isLongQuestion(question) ? schema(question) : undefined;
};

export const validateAndSerialize = (
  id: number,
  formValue: FormValue
): QuestionResponse | undefined => {
  if (!isNonEmpty(formValue)) return undefined;
  return { id, ...submissionValue(formValue) };
};
